import { Injectable } from '@angular/core'
import { Observable } from 'rxjs/Observable'

import { GraphqlService } from '../../graphql/graphql.service'
import { Course } from '../../../../../data/models'
import { DateHelper } from '../../../../helpers'

const PROGRAM_CODE_MAPPING = {
  193: 'LSAT'
}

@Injectable()
export class KtpEnrollmentRecommendationService {

  public constructor(
    private _graphQlService: GraphqlService,
    private _dateHelper: DateHelper
  ) {}

  public get(): Observable<any> {
    const courses = []
    const items = []
    let coursesObservable: Observable<Course[]>
    let itemObservable: Observable<Array<{}>>
    return this._graphQlService.query('enrollments_with_free_flag')
      .map((data) => {
        const trialEnrollments = this._getTrialEnrollments(data['result']['Enrollment'])
        trialEnrollments.forEach((trialEnrollment) => {
          if (this._isCourseEnrollment(trialEnrollment)) {
            courses.push(this._createCourseFromTrial(trialEnrollment))
          } else {
            items.push(this._createItemFormTrial(trialEnrollment))
          }
        })

        coursesObservable = courses.length > 0 ? Observable.zip(...courses) : Observable.of([])
        itemObservable = items.length > 0 ? Observable.zip(...items) : Observable.of([])
        return { courses: coursesObservable, items: itemObservable }
      })
  }

  private _getTrialEnrollments(enrollments) {
    return enrollments.filter((enrollment) => enrollment['KapClass']['Product']['isFreeTrial'])
  }

  private _createCourseFromTrial(trialEnrollment): Observable<Course> {
    return this._getEnrollmentByTrial(trialEnrollment['KapClass']['id'])
      .map((data) => this._createCourse(data['result']['Class'][0]))
  }

  private _createItemFormTrial(enrollment) {
    return this._getEnrollmentByTrial(enrollment['KapClass']['id'])
      .map((data) => {
        const itemData = data['result']['Class'][0]
        return {
          name: itemData['name'],
          cartUrl: this._determineItemCartUrl(itemData['id']),
          price: itemData['salePrice'],
          testline: this._getTestline(itemData['programId'])
        }
      })
  }

  private _getEnrollmentByTrial(trialEnrollmentId) {
    return this._graphQlService.query('enrollment_by_trial', { trial_class_id: trialEnrollmentId.toString()})
  }

  private _createCourse(courseData): Course {
    const course = new Course()
    course.name = courseData['name']
    course.price = courseData['salePrice']
    course.testLine = this._getTestline(courseData['programId'])
    course.schedule = this._formatSchedule(courseData)
    course.instructor = this._formatInstructor(courseData['Meeting'][0]['SessionFacilitator'])
    course.location = this._formatLocation(courseData)
    course.active = this._determineActive(courseData)
    course.cartUrl = this._determineCourseCartUrl(course, courseData['id'])
    course.deactivateOn = this._determineDeactivatedOn(courseData['Meeting'])
    return course
  }

  private _formatSchedule(data) {
    const startDate = this._dateHelper.get(data['startDate'])
    const endDate = this._dateHelper.get(data['endDate'])
    const [startTime, endTime] = this._convertSessionTime(data['Meeting'][0])

    return `${this._formatSessionDays(data['Meeting'])}<br>
            ${startDate.format('MMM. DD')} - ${endDate.format('MMM. DD')}<br>
            ${startTime.format('hh:mm A')} - ${endTime.format('hh:mm A z')}`
  }

  private _formatSessionDays(sessions): string {
    const days = sessions.map((session) => this._dateHelper.get(session['sessionDate']).format('dddd'))
    const uniqueDays = new Set(days)
    return Array.from(uniqueDays).join(', ')
  }

  private _formatInstructor(facilitators): string {
    const facilitator = facilitators.filter((facilitator) => facilitator['facilitatorStatus'] === 'Teacher')[0]
    return facilitator ? `${facilitator['teacher']['firstName']} ${facilitator['teacher']['lastName']}` : 'Instructor to be determined'
  }

  private _formatLocation(courseData): string {
    return courseData['isLiveOnlineEvent'] ? 'Online' : courseData['Facility']['Location']['city']
  }

  private _determineActive(courseData): boolean {
    return courseData['status'].toLowerCase() === 'released' && courseData['noWeb'] === false
  }

  private _determineDeactivatedOn(sessions): Date {
    const secondSession = sessions[1]
    return new Date(`${secondSession['sessionDate']} 00:00:00`)
  }

  private _determineCourseCartUrl(course: Course, mainCourseId: string): string {
    return course.active ?
      `//enroll.manhattanprep.com/shoppingcart?classids=${mainCourseId}` :
      `//manhattanprep.kaptest.com/${course.testLine.toLowerCase()}/classes/`
  }

  private _determineItemCartUrl(itemId): string {
    return `//enroll.manhattanprep.com/shoppingcart?classids=${itemId}`
  }

  private _isCourseEnrollment(ktpEnrollment): boolean {
    return ktpEnrollment['KapClass']['Meeting'].length > 0
  }

  private _getTestline(programCode): string {
    return PROGRAM_CODE_MAPPING[programCode] || ''
  }

  private _convertSessionTime(session): any[] {
    const today = this._dateHelper.get(new Date()).format('YYYY/MM/DD')
    const timezone = session['TimeZone']
    const offset = timezone['rawOffset'] + timezone['dstOffset']
    const startDate = this._dateHelper.get(`${today} ${session['sessionStartTime']} ${offset}`).tz(timezone['timeZoneId'])
    const endDate = this._dateHelper.get(`${today} ${session['sessionEndTime']} ${offset}`).tz(timezone['timeZoneId'])
    return [startDate, endDate]
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/data/services/product-recommendation/ktp-enrollment-recommendation/ktp-enrollment-recommendation.service.ts